import { db, type DbServerRef, type DbTable } from '../../api/db';
import { EmptyState, ErrorBox, Loading, TableScroller, useAsync } from '../../components/common';
import { formatBytes, formatCount, formatRelative } from '../../lib/format';
import { Bar, DatabasePicker, useServerDatabases } from './shared';

const latest = (a: string | null, b: string | null) => (!a ? b : !b ? a : a > b ? a : b);

export function TablesTab({
  server,
  database,
  onDatabase,
}: {
  server: DbServerRef;
  database: string;
  onDatabase: (db: string) => void;
}) {
  const dbs = useServerDatabases(server.id);
  const list = useAsync(
    async () => (database ? db.tables(server.id, database, 50) : null),
    [server.id, database]
  );
  const rows = list.data ?? [];
  const max = Math.max(1, ...rows.map((t) => t.totalBytes));
  const now = Date.now();
  return (
    <>
      <div className="dbx-toolbar">
        <DatabasePicker databases={dbs.names} value={database} onChange={onDatabase} />
        {database && list.data && <span className="dbx-muted">largest {rows.length} tables</span>}
      </div>
      <ErrorBox error={dbs.error} />
      <ErrorBox error={list.error} />
      {!database ? (
        <EmptyState
          card
          title="Pick a database"
          hint="Shows its largest tables with sizes and vacuum state."
        />
      ) : (
        <>
          <Loading loading={list.loading && !list.data} />
          {list.data && !rows.length && <EmptyState card title="No tables in this database" />}
          {!!rows.length && (
            <div className="nyk-card">
              <TableScroller>
                <table className="nyk-ui-table nyk-cards dbx-tables" data-testid="db-tables">
                  <thead>
                    <tr>
                      <th>Table</th>
                      <th className="nyk-ui-num">Rows (est.)</th>
                      <th className="nyk-ui-num">Total</th>
                      <th className="dbx-bar-col">
                        <span className="dbx-legend">
                          <i className="dbx-bar-fill dbx-seg-table" /> table{' '}
                          <i className="dbx-bar-fill dbx-seg-index" /> index
                        </span>
                      </th>
                      <th className="nyk-ui-num">Table</th>
                      <th className="nyk-ui-num">Indexes</th>
                      <th className="nyk-ui-num">Dead tuples</th>
                      <th>Last vacuum</th>
                      <th>Last analyze</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t: DbTable) => {
                      const vac = latest(t.lastVacuum, t.lastAutovacuum);
                      const deadShare = t.rowEstimate > 0 ? t.deadTuples / t.rowEstimate : 0;
                      return (
                        <tr key={`${t.schema}.${t.name}`} data-row={`${t.schema}.${t.name}`}>
                          <td className="nyk-card-title">
                            <span className="dbx-muted">{t.schema}.</span>
                            <b>{t.name}</b>
                          </td>
                          <td className="nyk-ui-num" data-label="Rows (est.)">
                            {formatCount(t.rowEstimate)}
                          </td>
                          <td className="nyk-ui-num" data-label="Total">
                            {formatBytes(t.totalBytes)}
                          </td>
                          <td className="dbx-bar-col" data-label="Split">
                            <Bar
                              max={max}
                              label={`${formatBytes(t.totalBytes)} total`}
                              parts={[
                                { value: t.tableBytes, className: 'dbx-seg-table', title: 'table' },
                                { value: t.indexBytes, className: 'dbx-seg-index', title: 'indexes' },
                                {
                                  value: Math.max(0, t.totalBytes - t.tableBytes - t.indexBytes),
                                  className: 'dbx-seg-other',
                                  title: 'toast / other',
                                },
                              ]}
                            />
                          </td>
                          <td className="nyk-ui-num" data-label="Table">
                            {formatBytes(t.tableBytes)}
                          </td>
                          <td className="nyk-ui-num" data-label="Indexes">
                            {formatBytes(t.indexBytes)}
                          </td>
                          <td className="nyk-ui-num" data-label="Dead tuples">
                            <span
                              className={
                                deadShare > 0.2 && t.deadTuples > 10_000 ? 'dbx-warn-text' : undefined
                              }
                            >
                              {formatCount(t.deadTuples)}
                            </span>
                          </td>
                          <td data-label="Last vacuum" title={vac ?? ''}>
                            {formatRelative(vac, now)}
                            {vac && vac === t.lastAutovacuum && <span className="dbx-muted"> (auto)</span>}
                          </td>
                          <td data-label="Last analyze" title={t.lastAnalyze ?? ''}>
                            {formatRelative(t.lastAnalyze, now)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          )}
        </>
      )}
    </>
  );
}
